import { PaginatedList, PaginationParams, PaginatedQueryParams } from "../types/pagination";
import { unwrapApiResponse } from "../utils/apiUtils";
import { api } from "./apiSlice";
import { 
  TrailerOwnershipType,
  OwnerInfoFormValues,
  LeaseFromInfoFormValues,
  LeaseToInfoFormValues
} from "../types/ownership";

export interface Trailer {
  id: number;
  unitNumber: string | null;
  isActive: boolean;
  isArchived: boolean;
  archivedAt: string | null;
  trailerType: string | null;
  size: string | null;
  trailerStatus: string | null;
  division: string | null;
  make: string | null;
  model: string | null;
  year: string | null;
  color: string | null;
  vin: string | null;
  platesNumber: string | null;
  createdAt: string;
  updatedAt: string | null;
}

// Lookup DTOs matching backend
export interface TrailerTypeDto {
  id: number;
  name: string;
}

export interface TrailerStatusDto {
  id: number;
  name: string;
}

export interface TrailerMakeDto {
  id: number;
  name: string;
}

export interface TrailerModelDto {
  id: number;
  name: string;
}

export interface DivisionDto {
  id: number;
  name: string;
}

// Lessor/Lessee DTOs (shared with vehicles)
export interface LessorDto {
  id: number;
  name: string;
}

export interface LesseeDto {
  id: number;
  name: string;
}

// New ownership DTOs 
export interface TrailerOwnerInfoDto extends OwnerInfoFormValues {}

export interface TrailerLeaseFromDto extends LeaseFromInfoFormValues {}

export interface TrailerLeaseToDto extends LeaseToInfoFormValues {}

// Request DTOs for Create/Update
export interface CreateTrailerRequestDto {
  // General Section
  unitNumber?: string | null;
  isActive?: boolean;
  startDate?: string | null;
  endDate?: string | null;
  size?: string | null;
  isTanker?: boolean;
  
  // Lookup fields
  trailerTypeId?: number | null;
  trailerTypeName?: string | null;
  trailerStatusId?: number | null;
  trailerStatusName?: string | null;
  divisionId?: number | null;
  divisionName?: string | null;

  // Equipment Info Section
  color?: string | null;
  year?: string | null;
  vin?: string | null;
  platesNumber?: string | null;
  makeId?: number | null;
  makeName?: string | null;
  modelId?: number | null;
  modelName?: string | null;

  // Reefer Section
  reeferMake?: string | null;
  reeferModel?: string | null;
  reeferVin?: string | null;
  purchaseDate?: string | null;
  purchaseAmount?: number | null;

  // Ownership Section - NEW STRUCTURE  
  ownershipType?: TrailerOwnershipType;
  ownerInfo?: TrailerOwnerInfoDto | null;
  leaseFromInfo?: TrailerLeaseFromDto | null;
  leaseToInfo?: TrailerLeaseToDto | null;

  // Legacy ownership fields for backward compatibility
  isCompanyOwned?: boolean;
  isLeased?: boolean;

  // Safety Section
  dotInspectionExpiration?: string | null;
  platesExpirationDate?: string | null;
  insuranceExpirationDate?: string | null;
  insuranceValue?: number | null;
  hasCustomWarning?: boolean;

  // Dispatch Section
  category?: string | null;

  // Comments Section
  comments?: string | null;
}

export interface UpdateTrailerRequestDto extends CreateTrailerRequestDto {
  // Same as create for now
}

// Response DTOs for detailed views
export interface TrailerResponseDto extends Trailer {
  // Additional properties from detail entities (matching actual API response camelCase)
  startDate: string | null;
  endDate: string | null;
  isTanker: boolean;
  
  // Equipment Info Section
  color: string | null;
  year: string | null;
  vin: string | null;
  platesNumber: string | null;
  // make and model are already in the base Trailer interface

  // Reefer Section
  reeferMake: string | null;
  reeferModel: string | null;
  reeferVIN: string | null;
  purchaseDate: string | null;
  purchaseAmount: number | null;

  // Ownership Section - NEW STRUCTURE
  ownershipType: TrailerOwnershipType;
  ownerInfo: TrailerOwnerInfoDto | null;
  leaseFromInfo: TrailerLeaseFromDto | null;
  leaseToInfo: TrailerLeaseToDto | null;

  // Legacy ownership fields for backward compatibility  
  isCompanyOwned: boolean;
  isLeased: boolean;

  // Safety Section
  dotInspectionExpiration: string | null;
  platesExpirationDate: string | null;
  insuranceExpirationDate: string | null;
  insuranceValue: number | null;
  hasCustomWarning: boolean;

  // Dispatch Section
  category: string | null;

  // Comments Section
  comments: string | null;

  // Include all available lookup options for dropdowns
  availableTrailerTypes: TrailerTypeDto[];
  availableTrailerStatuses: TrailerStatusDto[];
  availableTrailerMakes: TrailerMakeDto[];
  availableTrailerModels: TrailerModelDto[];
  availableDivisions: DivisionDto[];
  availableLessors: LessorDto[];
  availableLessees: LesseeDto[];
}

export interface TrailerLookupDataDto {
  trailerTypes: TrailerTypeDto[];
  trailerStatuses: TrailerStatusDto[];
  trailerMakes: TrailerMakeDto[];
  trailerModels: TrailerModelDto[];
  divisions: DivisionDto[];
  lessors: LessorDto[];
  lessees: LesseeDto[];
}

// API Response type is now imported from utils
// export interface ApiResponse<T> - moved to utils/apiUtils.ts

export const trailersApi = api.injectEndpoints({
  endpoints: (builder) => ({
    getTrailers: builder.query<Trailer[], void>({
      query: () => "trailers",
      providesTags: ["Trailers"],
      transformResponse: unwrapApiResponse,
    }),
    getTrailersPaginated: builder.query<
      PaginatedList<Trailer>,
      PaginationParams
    >({
      query: (params) => ({
        url: "trailers",
        params: { ...params, paginated: true },
      }),
      providesTags: ["Trailers"],
      transformResponse: unwrapApiResponse,
    }),
    getTrailersPaginatedWithSearch: builder.query<
      PaginatedList<Trailer>,
      PaginatedQueryParams
    >({
      query: (queryParams) => {
        const params: any = {
          pageNumber: queryParams.pageNumber,
          pageSize: queryParams.pageSize,
          paginated: true,
        };
        if (queryParams.filters && Object.keys(queryParams.filters).length > 0) {
          Object.entries(queryParams.filters).forEach(([key, value]) => {
            if (value !== null && value !== undefined && value !== '') {
              params[`filters[${key}]`] = value;
            }
          });
        }
        return { url: "trailers", params };
      },
      providesTags: ["Trailers"],
      transformResponse: unwrapApiResponse,
    }),
    getTrailerById: builder.query<TrailerResponseDto, number>({
      query: (id) => `trailers/${id}`,
      providesTags: (result, error, id) => [{ type: "Trailers", id }],
      transformResponse: unwrapApiResponse,
    }),
    getTrailerLookups: builder.query<TrailerLookupDataDto, void>({
      query: () => "trailers/lookups",
      providesTags: ["TrailerLookups"],
      transformResponse: unwrapApiResponse,
    }),
    createTrailer: builder.mutation<TrailerResponseDto, CreateTrailerRequestDto>({
      query: (trailer) => ({
        url: "trailers",
        method: "POST",
        body: trailer,
      }),
      invalidatesTags: ["Trailers"],
      transformResponse: unwrapApiResponse,
    }),
    updateTrailer: builder.mutation<TrailerResponseDto, { id: number; trailer: UpdateTrailerRequestDto }>({
      query: ({ id, trailer }) => ({
        url: `trailers/${id}`,
        method: "PUT",
        body: trailer,
      }),
      invalidatesTags: (result, error, { id }) => [
        "Trailers",
        { type: "Trailers", id },
      ],
      transformResponse: unwrapApiResponse,
    }),
    deleteTrailer: builder.mutation<boolean, number>({
      query: (id) => ({
        url: `trailers/${id}`,
        method: "DELETE",
      }),
      invalidatesTags: (result, error, id) => [
        "Trailers",
        { type: "Trailers", id },
      ],
      transformResponse: unwrapApiResponse,
    }),
    archiveTrailer: builder.mutation<void, number>({
      query: (id) => ({
        url: `trailers/${id}/archive`,
        method: "POST",
      }),
      invalidatesTags: ["Trailers", "Documents", "AnyTrackLocations"],
    }),
    restoreTrailer: builder.mutation<void, number>({
      query: (id) => ({
        url: `trailers/${id}/restore`,
        method: "POST",
      }),
      invalidatesTags: ["Trailers", "Documents", "AnyTrackLocations"],
    }),
  }),
});

export const {
  useGetTrailersQuery,
  useGetTrailersPaginatedQuery,
  useGetTrailersPaginatedWithSearchQuery,
  useGetTrailerByIdQuery,
  useGetTrailerLookupsQuery,
  useCreateTrailerMutation,
  useUpdateTrailerMutation,
  useDeleteTrailerMutation,
  useArchiveTrailerMutation,
  useRestoreTrailerMutation,
} = trailersApi;